import { useEffect, useRef, useState } from "react";
import AnchoredPanel from "../../../../GlobalComponents/AnchoredPanel/AnchoredPanel";

/**
 * A small list anchored under the cell that opened it. Deliberately not a
 * modal: it does not trap focus, dim the page or move the row, so the grid
 * behind it stays readable while a value is picked.
 *
 * Closing on an outside click and on Escape are document-level concerns, so
 * they are the one place this component reaches for effects.
 *
 * @param {object} props
 * @param {{ current: HTMLElement|null }} props.anchorRef the cell control it opens from
 * @param {string} props.title
 * @param {string[]} props.options
 * @param {string[]} props.selected already on the cell; picking one removes it
 * @param {(value: string) => void} props.onPick
 * @param {() => void} props.onClose
 * @param {boolean} [props.creatable] offers a box for a value that is not in the list
 */
export default function InlinePicker({ anchorRef, title, options, selected, onPick, onClose, creatable }) {
  const firstOptionRef = useRef(null);
  const [typed, setTyped] = useState("");

  /** A typed value already listed, in any case, is that value; one already on the cell just closes. */
  const create = () => {
    const value = typed.trim();
    if (!value) return;
    const known = [...options, ...selected].find((option) => option.toLowerCase() === value.toLowerCase());
    if (known && selected.includes(known)) onClose();
    else onPick(known ?? value);
  };

  useEffect(() => {
    if (firstOptionRef.current) firstOptionRef.current.focus();
  }, []);


  return (
    <AnchoredPanel anchorRef={anchorRef} onClose={onClose} className="kgb-picker" role="group">
      <p className="kgb-picker-title">{title}</p>
      {options.length === 0 ? (
        <p className="kgb-picker-empty">Nothing to choose from yet</p>
      ) : (
        options.map((option, index) => (
          <button
            key={option}
            type="button"
            ref={index === 0 ? firstOptionRef : null}
            className="kgb-picker-option"
            aria-pressed={selected.includes(option)}
            onClick={() => onPick(option)}
          >
            {option}
          </button>
        ))
      )}
      {creatable ? (
        <input
          type="text"
          className="kgb-picker-create"
          placeholder="Type another and press Enter"
          aria-label={`Add another ${title.toLowerCase()}`}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            e.preventDefault();
            create();
          }}
        />
      ) : null}
    </AnchoredPanel>
  );
}
